import { isEnterKey } from 'src/scripts/utils/key'
import { QuickView } from '../../../sections/product/quick-view'
import { debounce } from '../../../utils/debounce'
import { hideElement } from '../../../utils/dom'
import { Card } from '../card/card'
import { ColorSwatches } from './product-card-color-swatches'
import { $el, $list } from 'src/scripts/utils/utils'
import { isMobile } from 'src/scripts/utils/check-media'
import { CarouselComponent } from '../../carousel'

const QUICK_VIEW_BUTTON_SELECTOR = '[data-product-card-quick-view-button]'
const PRELOADER_SELECTOR = '[data-product-card-preloader]'
const BUTTONS_SELECTOR = '[data-product-card-buttons]'
const FEATURED_MEDIA_SELECTOR = '[data-product-card-featured-image]'
const COLOR_SWATCHES_SELECTOR = '[data-product-card-color-swatches]'
const INNER_SELECTOR = '[data-product-card-inner]'
const QUICK_VIEW_SELECTOR = '#Quick-view'
const MAIN_MEDIA_SELECTOR = '[data-main-media]'

export class ProductCard extends Card {
  resizeObserver: ResizeObserver
  buttonsWidth: number = 0
  buttonsHeight: number = 0
  carouselInited: boolean
  playingVideo: HTMLVideoElement

  mountComponent(): void {
    const quickViewBtn = $el(QUICK_VIEW_BUTTON_SELECTOR, this)
    const featuredImages = $list(FEATURED_MEDIA_SELECTOR, this) as
      | HTMLMediaElement[]
      | HTMLImageElement[]

    this.resizeObserver = new ResizeObserver(this.handleResize)
    this.resizeObserver.observe(this)

    featuredImages.forEach(featuredImage => {
      if (
        featuredImage.complete ||
        featuredImage.readyState === 4 ||
        featuredImage.tagName === 'IFRAME'
      ) {
        this.handleMediaLoad()
      } else {
        this.addListener(featuredImage, 'load', this.handleMediaLoad)
        this.addListener(featuredImage, 'loadeddata', this.handleMediaLoad)
      }
    })

    this.addListener(quickViewBtn, 'click', this.handleQuickViewBtnClick)
    this.addListener(quickViewBtn, 'keydown', this.handleQuickViewBtnKeyDown)
    this.addListener(this, 'mouseenter', this.handleProductCardEnter)
  }

  unmountComponent(): void {
    this.resizeObserver.disconnect()
  }

  handleQuickViewBtnKeyDown = async (event: KeyboardEvent): Promise<void> => {
    if (isEnterKey(event)) {
      event.preventDefault()
      this.callQuickView(true)
    }
  }

  handleQuickViewBtnClick = async (): Promise<void> => {
    this.callQuickView(true)
  }

  handleProductCardEnter = () => {
    this.callQuickView(false)
  }

  async callQuickView(withOpen: boolean) {
    const quickView: QuickView = $el(QUICK_VIEW_SELECTOR)
    const url = this.dataset.url
    const id = this.dataset.id

    if (!quickView || !url || !id) {
      return
    }

    await quickView.requestProductFromUrl(url, id)
    if (withOpen) {
      this.setQuickViewLoading(true)
      await quickView.openAndRenderProductByUrl(url)
      this.setQuickViewLoading(false)
    }
  }

  handleResize = debounce((): void => {
    const buttons = $el(BUTTONS_SELECTOR, this)
    const swatches: ColorSwatches = $el(COLOR_SWATCHES_SELECTOR, this)

    if (buttons) {
      this.setButtonsOverflow()
    }
    if (swatches) {
      swatches.update()
    }

    if (isMobile()) {
      this.initMobileCarousel()
    }
  }, 250)

  handleMediaLoad = (): void => {
    const loader = $el(PRELOADER_SELECTOR, this)
    hideElement(loader)
  }

  initMobileCarousel = () => {
    if (this.carouselInited) {
      return
    }

    const carousel: CarouselComponent = $el('carousel-component', this)
    if (!carousel) {
      return
    }

    const video = $el('video', carousel)

    if (video) {
      carousel.embla?.on('select', this.handleCarouselSelect)
      this.carouselInited = true
    }
  }

  handleCarouselSelect = debounce(embla => {
    if (this.playingVideo) {
      this.playingVideo.pause()
      this.playingVideo = null
    }

    const currentSlide =
      embla.slideNodes()[embla.selectedScrollSnap()].firstElementChild

    if (currentSlide.tagName === 'VIDEO') {
      currentSlide.play()
      this.playingVideo = currentSlide
    }
  }, 100)

  setButtonsOverflow(): void {
    const buttons = $el(BUTTONS_SELECTOR, this)
    const inner = $el(INNER_SELECTOR, this)

    if (!buttons) {
      return
    }

    if (this.offsetWidth < this.buttonsWidth) {
      return
    }

    const isWidthOverflow = buttons.offsetWidth > inner.offsetWidth
    const isHeightOverflow = buttons.offsetHeight > inner.offsetHeight

    if (isWidthOverflow && buttons.offsetWidth > this.buttonsWidth) {
      this.buttonsWidth = buttons.offsetWidth
    }

    if (isHeightOverflow && buttons.offsetHeight > this.buttonsHeight) {
      this.buttonsHeight = buttons.offsetHeight
    }

    buttons.classList.toggle('is-width-overflowed', isWidthOverflow)
    buttons.classList.toggle('is-height-overflowed', isHeightOverflow)
  }

  setQuickViewLoading(isLoading: boolean) {
    const quickViewBtn = $el(QUICK_VIEW_BUTTON_SELECTOR, this)
    if (quickViewBtn) {
      quickViewBtn.classList.toggle('loading', isLoading)
      quickViewBtn.toggleAttribute('disabled', isLoading)
    }
  }

  getMainMedia() {
    return $list(MAIN_MEDIA_SELECTOR, this).filter(
      item => item.offsetWidth > 0,
    )[0]
  }
}
